import React, { useEffect, useState } from "react";
import PropTypes from "prop-types";
import { useTable, useSortBy } from "react-table";
import cx from "classnames";

import LpgMoveUpSolid from "@/icons/Lpg/MoveUpSolid";
import { Icon } from "../Icon";
import { ThemeConsumer, Theme } from "../Theme";

import styles from "./Table.scss";

const Cell = ({ cell: { value } }) => value;
Cell.displayName = "Table.Cell";

export function Table({ userColumns, data, ...otherProps }) {
  // Add styles for sort arrows
  const sortClassAsc = (column) =>
    cx(styles.sort_icon, {
      [styles.sort_active]: column.isSorted && !column.isSortedDesc,
    });

  const sortClassDesc = (column) =>
    cx(styles.sort_icon, {
      [styles.sort_icon__desc]: true,
      [styles.sort_active]: column.isSorted && column.isSortedDesc,
    });

  // Generate class to hide for view
  const hideClasses = (column) => {
    const breakpoint = (point) =>
      column.hide && column.hide.length > 0
        ? column.hide.includes(point)
        : false;
    return cx({
      [styles.hide_xs]: breakpoint("xs"),
      [styles.hide_s]: breakpoint("s"),
      [styles.hide_m]: breakpoint("m"),
      [styles.hide_l]: breakpoint("l"),
      [styles.hide_xl]: breakpoint("xl"),
    });
  };

  // Change default function to render component in cell
  const columnsFromUserColumns = () =>
    userColumns.map((column) => ({
      ...column,
      Cell,
    }));
  const [columns, setColumns] = useState(columnsFromUserColumns());
  useEffect(() => {
    setColumns(columnsFromUserColumns());
  }, [userColumns]);

  const isHeadLess = columns.every((column) => !column.Header);

  const { getTableProps, headerGroups, rows, prepareRow } = useTable(
    {
      columns,
      data,
    },
    useSortBy
  );

  const tableProps = getTableProps();
  delete tableProps.role;
  return (
    <ThemeConsumer>
      {(theme) => (
        <>
          <div
            className={`${styles.wrapper} ${
              theme === Theme.dark ? styles.theme_dark : ""
            }`}
            {...otherProps}
          >
            <table {...tableProps} className={styles.table}>
              {!isHeadLess && (
                <thead>
                  {headerGroups.map((headerGroup) => (
                    <tr
                      {...headerGroup.getHeaderGroupProps()}
                      className={styles.row}
                      key={headerGroup.headers.reduce(
                        (key, header) => key.concat(`-${header.Header}`),
                        "key"
                      )}
                    >
                      {headerGroup.headers.map((column) => {
                        const activeIconColor =
                          theme === Theme.dark ? "#FFF" : "#4B4B4B";
                        const inactiveIconColor =
                          theme === Theme.dark ? "#4B4B4B" : "";
                        const upIconColor =
                          column.isSorted && !column.isSortedDesc
                            ? activeIconColor
                            : inactiveIconColor;
                        const downIconColor =
                          column.isSorted && column.isSortedDesc
                            ? activeIconColor
                            : inactiveIconColor;
                        return (
                          <th
                            scope="col"
                            key={column.index}
                            {...column.getHeaderProps(
                              !column.disableSorting &&
                                column.getSortByToggleProps()
                            )}
                            className={`${styles.cell_header} ${hideClasses(
                              column
                            )}`}
                          >
                            {column.disableSorting ? (
                              <span className={styles.label}>
                                {column.render("Header")}
                              </span>
                            ) : (
                              <div className={styles.sort}>
                                <button
                                  type="button"
                                  className={styles.sort_button}
                                >
                                  <span className={styles.sort_icons}>
                                    <span className={sortClassAsc(column)}>
                                      <Icon
                                        icon={LpgMoveUpSolid}
                                        size={Icon.Size.parent}
                                        color={upIconColor}
                                      />
                                    </span>
                                    <span className={sortClassDesc(column)}>
                                      <Icon
                                        icon={LpgMoveUpSolid}
                                        size={Icon.Size.parent}
                                        color={downIconColor}
                                      />
                                    </span>
                                  </span>
                                  <span className={styles.label_sort}>
                                    {column.render("Header")}
                                  </span>
                                </button>
                              </div>
                            )}
                          </th>
                        );
                      })}
                    </tr>
                  ))}
                </thead>
              )}
              <tbody>
                {rows.map(
                  (row) =>
                    prepareRow(row) || (
                      <tr {...row.getRowProps()} className={styles.row}>
                        {row.cells.map((cell) => (
                          <td
                            className={`${styles.cell} ${hideClasses(
                              cell.column
                            )}`}
                            key={`key-${cell.row.id}-${cell.column.id}`}
                          >
                            {cell.render("Cell")}
                          </td>
                        ))}
                      </tr>
                    )
                )}
              </tbody>
            </table>
          </div>
        </>
      )}
    </ThemeConsumer>
  );
}

// Props
Table.propTypes = {
  userColumns: PropTypes.arrayOf(PropTypes.object),
  data: PropTypes.arrayOf(PropTypes.object),
};

Table.defaultProps = {
  userColumns: [],
  data: [],
};

Table.displayName = "Table";

// Help function to sort component by value
export const componentValue = (rowA, rowB, columnID, type) => {
  let a = rowA.values[columnID];
  let b = rowB.values[columnID];

  // Check if component is React Element
  if (
    a.$$typeof === Symbol.for("react.element") &&
    b.$$typeof === Symbol.for("react.element")
  ) {
    a = a.props && a.props.children;
    b = b.props && b.props.children;
  }

  // Change date format
  if (type === "date") {
    a = new Date(a.replace(/(\d{2}).(\d{2}).(\d{4})/, "$2/$1/$3"));
    b = new Date(b.replace(/(\d{2}).(\d{2}).(\d{4})/, "$2/$1/$3"));
  }

  if (a === b) {
    return 0;
  }

  return a > b ? 1 : -1;
};

// Help function to sort component by date
export const componentDate = (rowA, rowB, columnID) =>
  componentValue(rowA, rowB, columnID, "date");

export default Table;
